<template>
    <div class="contact-distributions-by-platform" :class="{ fullscreen: fullScreen }">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>pie_chart</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>

        <a-card :loading="loading" :bordered="false" class="chart-card">
            <a-empty v-if="!loading && (!data || data.length === 0)" />
            <template v-else>
                <div v-if="!tableMode" class="chart-wrapper" ref="chartWrapper">
                    <v-chart :options="chartOptions" autoresize ref="chart" />
                </div>
                <div v-else class="table-wrapper">
                    <TableView :cols="getCols()" :rows="getRows()" />
                </div>
            </template>
        </a-card>

          <div class="stats">
            <StatCard
                :title="$t('report.mostUsedPlatform')"
                :value="getStats().maxPlatform"
            />
            <StatCard
                :title="$t('report.leastUsedPlatform')"
                :value="getStats().minPlatform"
            />
        </div>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import { expertToExcel } from "../../../utils/utils";
import fscreen from "fscreen";

const platformColors = {
    supsis: "#1890ff",
    email: "#13c2c2",
    whatsapp: "#52c41a",
    web: "#722ed1",
    mobile: "#eb2f96",
    instagram: "#fa8c16",
    telegram: "#2f54eb",
    google: "#fa541c",
    messenger: "#1890ff",
    calendar: "#faad14",
    "meta-leads": "#eb2f96",
    trendyol: "#ff4d4f",
    linkedin: "#1890ff",

};

const platformNames = {
    supsis: "Supsis",
    email: "E-posta",
    whatsapp: "WhatsApp",
    web: "Web",
    mobile: "Mobil",
    instagram: "Instagram",
    "instagram-v2": "Instagram V2",
    telegram: "Telegram",
    google: "Google",
    messenger: "Messenger",
    calendar: "Takvim",
    "meta-leads": "Meta Leads",
    trendyol: "Trendyol",
    linkedin: "LinkedIn",
};

export default {
    name: "ContactDistributionsByPlatform",
    components: {
        IconButton,
        StatCard,
        TableView
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            tableMode: false,
            fullScreen: false,
            cancels: [],
            loadingInstance: null
        };
    },
    computed: {
        chartOptions() {
            if (!this.data || this.data.length === 0) return {};

            const sortedData = _.orderBy(this.data, ["count"], ["desc"]);

            return {
                title: {
                    text: this.title,
                    left: "center",
                },
                tooltip: {
                    trigger: "item",
                    formatter: "{b}: {c} ({d}%)",
                },
                series: [
                    {
                        type: "pie",
                        radius: "60%",
                        center: ["50%", "50%"],
                        data: sortedData.map(item => ({
                            name: platformNames[item.platform] || item.platform,
                            value: item.count,
                            itemStyle: {
                                color: platformColors[item.platform],
                            },
                        })),
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    },
                ],
            };
        },
    },
    methods: {
        formatStat(value) {
            if (isNaN(value) || value === null || value === undefined) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("report.contact")
            };
        },
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-platform");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("report.platform"),
                    dataIndex: "platform",
                    key: "platform",
                },
                {
                    title: this.$t("report.contactCount"),
                    dataIndex: "count",
                    key: "count",
                }
            ];
        },
        getRows() {
            return (this.data || []).map(item => ({
                platform: platformNames[item.platform] || item.platform,
                count: item.count
            }));
        },
        getStats() {
            const items = this.data || [];
            const maxItem = _.maxBy(items, 'count');
            const minItem = _.minBy(items, 'count');

            return {
                maxPlatform: maxItem ? (platformNames[maxItem.platform] || maxItem.platform) : this.$t("report.noEnoughtData"),
                minPlatform: minItem ? (platformNames[minItem.platform] || minItem.platform) : this.$t("report.noEnoughtData"),
                total: _.sumBy(items, 'count')
            };
        },
        exportToExcel() {
            const excelData = this.getRows();
            expertToExcel(excelData, `platform_distributions_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        }
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".contact-distributions-by-platform"),
            name: "wave",
        });
        if (this.loading) {
            this.loadingInstance.show();
        }
    },
    created() {
        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach(cancel => cancel());
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.contact-distributions-by-platform {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }
    }

    .chart-wrapper {
        width: 100%;
        height: 400px;
        ::v-deep .echarts {
            width: 100% !important;
        }
    }

    .table-wrapper {
        height: 400px;
        overflow: auto;
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>
